import React from "react";
import { Upload, CloudUpload, FileText, FileSpreadsheet, File } from "lucide-react";
import Button from "../shared/Button";
import ButtonWithPopover from "../shared/ButtonWithPopover";
import type { MenuItem } from "../shared/ButtonWithPopover";
import { useFilterStore } from "../../store/filterStore";
import { useInstanceStore } from "../../store/useInstanceStore";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import type { Instance } from "../table/types";

interface PriceComparisonProps {
  className?: string;
}

const PriceComparison: React.FC<PriceComparisonProps> = ({
  className = "",
}) => {
  const region = useFilterStore((state) => state.region);
  const pricingModel = useFilterStore((state) => state.pricingModel);
  const operatingSystem = useFilterStore((state) => state.operatingSystem);
  const timeUnit = useFilterStore((state) => state.timeUnit);

  const filterData = useInstanceStore((state) => state.filterData);
  const exportCount = filterData.length;

  const handleUpload = () => {};

  // Helper function to get table data
  const getTableData = () => {
    const tableColumnHeaders = [
      "Instance Type",
      "Instance Family",
      "vCPUs",
      "Memory (GB)",
      "CPU Architecture",
      "AWS Price",
      "GCP Price",
      "Azure Price",
      "Best",
    ];

    const tableRows = filterData.map((item: Instance) => [
      item.instanceType,
      item.instanceFamily || "-",
      item.vcpus,
      item.memory,
      item.cpuArchitecture || "-",
      typeof item.aws === "number" ? item.aws : item.aws || "-",
      typeof item.gcp === "number" ? item.gcp : item.gcp || "-",
      typeof item.azure === "number" ? item.azure : item.azure || "-",
      item.best || "-",
    ]);

    return { headers: tableColumnHeaders, rows: tableRows };
  };

  // ✅ PDF Export
  const handleExportPDF = () => {
    if (!filterData.length) {
      alert("No data to export!");
      return;
    }

    const doc = new jsPDF();
    const { headers, rows } = getTableData();

    // ---------- PDF HEADER ----------
    doc.setFontSize(16);
    doc.text("Cloud Instance Price Comparison", 14, 15);

    doc.setFontSize(11);
    doc.text(`Region: ${region}`, 14, 25);
    doc.text(`Pricing Model: ${pricingModel}`, 14, 31);
    doc.text(`OS: ${operatingSystem}`, 14, 37);
    doc.text(`Time Unit: ${timeUnit}`, 14, 43);

    // ---------- TABLE ----------
    autoTable(doc, {
      startY: 50,
      head: [headers],
      body: rows,
      styles: {
        fontSize: 9,
        cellPadding: 2,
      },
      headStyles: {
        fillColor: [0, 102, 204],
        textColor: [255, 255, 255],
        halign: "center",
      },
    });

    // ---------- SAVE PDF ----------
    doc.save(`instance-comparison.pdf`);
  };

  // ✅ CSV Export
  const handleExportCSV = () => {
    if (!filterData.length) {
      alert("No data to export!");
      return;
    }

    const { headers, rows } = getTableData();

    // Create CSV content
    const csvContent = [
      headers.join(","),
      ...rows.map((row) =>
        row.map((cell) => {
          // Escape commas and quotes in cell values
          const cellValue = String(cell || "");
          if (cellValue.includes(",") || cellValue.includes('"') || cellValue.includes("\n")) {
            return `"${cellValue.replace(/"/g, '""')}"`;
          }
          return cellValue;
        }).join(",")
      ),
    ].join("\n");

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `instance-comparison.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ✅ Excel Export
  const handleExportExcel = () => {
    if (!filterData.length) {
      alert("No data to export!");
      return;
    }

    const { headers, rows } = getTableData();

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);

    // Set column widths
    const colWidths = headers.map((_, index) => {
      const maxLength = Math.max(
        headers[index].length,
        ...rows.map((row) => String(row[index] || "").length)
      );
      return { wch: Math.min(maxLength + 2, 50) };
    });
    ws["!cols"] = colWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, "Instance Comparison");

    // Add metadata sheet
    const metadataSheet = XLSX.utils.aoa_to_sheet([
      ["Cloud Instance Price Comparison"],
      [""],
      ["Region", region],
      ["Pricing Model", pricingModel],
      ["Operating System", operatingSystem],
      ["Time Unit", timeUnit],
      [""],
      ["Export Date", new Date().toLocaleString()],
    ]);
    XLSX.utils.book_append_sheet(wb, metadataSheet, "Metadata");

    // Save file
    XLSX.writeFile(wb, `instance-comparison.xlsx`);
  };

  // Menu items for the popover
  const exportMenuItems: MenuItem[] = [
    {
      id: "pdf",
      label: "Export as PDF",
      icon: <FileText className="w-4 h-4" />,
      onClick: handleExportPDF,
      divider: true,
    },
    {
      id: "csv",
      label: "Export as CSV",
      icon: <File className="w-4 h-4" />,
      onClick: handleExportCSV,
      divider: true,
    },
    {
      id: "excel",
      label: "Export as Excel",
      icon: <FileSpreadsheet className="w-4 h-4" />,
      onClick: handleExportExcel,
      divider: true,
    },
  ];

  return (
    <div className={`bg-white border-b border-gray-200 ${className}`}>
      <div className="flex items-center justify-between px-6 py-3 w-full">
        {/* Left Section - Title */}
        <div className="flex items-center gap-2">
          <svg
            className="w-5 h-5 text-gray-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 
 01-2-2z"
            />
          </svg>
          <h1 className="text-lg font-medium text-gray-900">
            Price Comparison
          </h1>
        </div>

        {/* Right Buttons */}
        <div className="flex items-center gap-3 ml-auto">
          <Button
            variant="light"
            size="md"
            className="!hidden"
            leftIcon={<Upload className="w-4 h-4" />}
            onClick={handleUpload}
          >
            Upload Your AWS Inventory
          </Button>

          <ButtonWithPopover
            button={
              <Button
                variant="light"
                size="md"
                leftIcon={<CloudUpload className="w-4 h-4" />}
              >
                Export ({exportCount})
              </Button>
            }
            menuItems={exportMenuItems}
            placement="left"
            align="bottom"
          />
        </div>
      </div>

      <div className="px-6 py-2 text-sm text-gray-600">
        <strong>Current Filters:</strong> Region: {region}, Pricing:{" "}
        {pricingModel}, OS: {operatingSystem}, Time Unit: {timeUnit}
      </div>
    </div>
  );
};

export default PriceComparison;
